<template>
  <div
    class="incremental-add-beneficiary-container"
    style="padding-right: 2rem; padding-left: 2rem; padding-bottom: 5rem"
  >
    <div class="s-flex s-gap-4 s-mt-4">
      <InputField
        :label="$t('addBeneficiary.businessName')"
        id="business-name"
        :show-mandatory="true"
        v-if="!isControlPerson && getBeneficialOwnerType(i).value === 'ENTITY'"
        :value="getBusinessName(i).value"
        :set-value="(event) => setBusinessName({i, event})"
        :error="getErrorByField(i, StakeholderField.BUSINESS_NAME)"
        class="s-flex-1"
      />
      <div class="s-flex-1">
        <!-- placeholder for 50% width share-->
      </div>
    </div>

    <div class="personal-details s-mb-2">
      <div class="s-text-lg s-font-semibold s-mt-4 s-text-left">
        {{ personDetailsTitle }}
      </div>
      <div class="personal-details-form s-flex s-flex-col s-gap-4 s-mt-4">
        <div class="s-flex s-gap-4 s-w-full">
          <InputField
            class-name="s-flex-1"
            :label="$t('addBeneficiary.firstName')"
            id="first-name"
            :show-mandatory="true"
            :value="getFirstName(i).value"
            :set-value="(event) => setFirstName({i, event})"
            :error="getErrorByField(i, StakeholderField.FIRST_NAME)"
            :max-length="getMax(getFirstName(i).apiPath)"
          />
          <InputField
            class-name="s-flex-1"
            :label="$t('addBeneficiary.lastName')"
            :value="getLastName(i).value"
            id="last-name"
            :show-mandatory="true"
            :set-value="(event) => setLastName({i, event})"
            :error="getErrorByField(i, StakeholderField.LAST_NAME)"
            :max-length="getMax(getLastName(i).apiPath)"
          />
        </div>
        <div class="s-flex s-gap-4 s-w-full">
          <InputField
            :label="$t('addBeneficiary.jobTitle')"
            :value="getJobTitle(i).value"
            id="job-title"
            :show-mandatory="true"
            class-name="s-flex-1"
            :set-value="(event) => setJobTitle({i, event})"
            :error="getErrorByField(i, StakeholderField.JOB_TITLE)"
          />

          <DateInput
            :label="$t('addBeneficiary.dateOfBirth')"
            :value="getFormattedDate(getDateOfBirth(i).value)"
            id="date-of-birth"
            :show-mandatory="true"
            :set-value="(date) => setDateOfBirth({i, value: date})"
            :error="getErrorByField(i, StakeholderField.DATE_OF_BIRTH)"
            :max-date="maxDateOfBirth"
            class="s-flex-1"
          />
        </div>
        <div class="s-flex s-gap-4 s-w-full">
          <InputField
            :label="$t('addBeneficiary.emailAddress')"
            id="email"
            :value="getEmailAddress(i).value"
            :show-mandatory="true"
            :set-value="(event) => setEmailAddress({i, event})"
            :error="getErrorByField(i, StakeholderField.EMAIL_ADDRESS)"
            class="fs-mask s-flex-1"
          />

          <PhoneInput
            :label="$t('addBeneficiary.phoneNumber')"
            :value="getPhoneNumber(i).formattedValue"
            id="phone-number"
            :disable-country-code="false"
            :country-code="getPhoneCountryCode(i)"
            :show-mandatory="true"
            :set-value="(countryCode, value) => setPhoneNumber({i, countryCode, value})"
            :error="getErrorByField(i, StakeholderField.PHONE_NUMBER)"
            class="fs-mask s-flex-1"
          />
        </div>
      </div>
    </div>

    <Address
      :getters="{
        getAddressLine1: () => getAddressLine1(i),
        getAddressLine2: () => getAddressLine2(i),
        getAddressCity: () => getAddressCity(i),
        getAddressState: () => getAddressState(i),
        getAddressZip: () => getAddressZip(i),
        getAddressCountry: () => getAddressCountry(i),
        getErrorByField: (field) => getErrorByField(i, field),
        getApi: getApi
      }"
      :setters="{
        setAddressLine1: (event) => setAddressLine1({i, event}),
        setAddressLine2: (event) => setAddressLine2({i, event}),
        setAddressCity: (event) => setAddressCity({i, event}),
        setAddressState: (value) => setAddressState({i, value}),
        setAddressZip: (event) => setAddressZip({i, event}),
        setAddressCountry: (value) => setAddressCountry({i, value}),
        setTaxId: (event) => setTaxId({i, event})
      }"
    >
      <div slot="header" class="address-header s-text-lg s-font-semibold">
        {{ $t('address.title') }}
      </div>
      <div class="separator"></div>
    </Address>

    <div class="ownership-container" style="margin-top: 32px;" v-if="shouldShowApiField('taxId', i)">
      <div class="sub-header s-text-left s-text-lg s-font-semibold s-mt-4">
        {{ $t('addBeneficiary.personalIdentification') }}
      </div>
      <div class="s-flex s-gap-4 s-mt-4">
        <InputField
          v-if="shouldShowApiField('taxId', i)"
          :label="$t(getTaxId(i).label[getAddressCountry(i).value]) || $t(getTaxId(i).label.default)"
          id="tax-id"
          format=""
          :value="getTaxId(i).value"
          :set-value="(event) => setTaxId({i, event})"
          :error="getErrorByField(i, StakeholderField.TAX_ID)"
          :show-mandatory="true"
          :max-length="getMaxLengthForTaxId()"
          :hint="$t(getTaxId(i).instruction[getAddressCountry(i).value]) || $t(getTaxId(i).instruction.default)"
          class="fs-mask s-flex-1"
        />
      </div>
    </div>
  </div>
</template>
<script>
import InputField from "@/components/common/InputField";
import PhoneInput from "@/components/common/PhoneInput";
import DateInput from "@/components/common/DateInput";
import {mapActions, mapGetters} from "vuex";
import validations from "@/app/validators/api/openapi";
import Address from "@/components/onboarding/Address";
import {
  additionalOwnerPercentageOwnership,
  controlPersonPercentageOwnership,
  StakeholderField,
} from "@/store/modules/onboarding/stakeholderDetails";
import {EventBus} from "@/events/event-bus";
import {Country, FormEvents} from "@/app/utils/common/constants";
import BeneficialOwnerTypes from "@/data/beneficial-owner-types";
import {CountryCodeListSortedByName} from "@/data/country-code-list";

export default {
  name: "IncrementalOnboardingAddBeneficiary",
  components: {Address, DateInput, PhoneInput, InputField},
  props: {
    i: {
      type: Number,
      required: true,
    },
    actionAtFooter: {
      type: Boolean,
      required: false,
      default: false,
    },
    isControlPerson: Boolean,
    editMode: Boolean,
    readOnly: {
      type: Boolean,
      required: false,
    },
  },
  data: () => {
    return {
      additionalOwnerPercentageOwnership,
      controlPersonPercentageOwnership,
      BeneficialOwnerTypes,
    }
  },
  computed: {
    StakeholderField() {
      return StakeholderField
    },
    personDetailsTitle() {
      return this.isControlPerson ? this.$t('addBeneficiary.personalDetails') :
          this.$t('addBeneficiary.beneficialOwnerDetails');
    },
    personDetailsInstructions() {
      return this.isControlPerson ? this.$t('addBeneficiary.personalDetailsInstructions') :
          this.$t('addBeneficiary.beneficialOwnerDetailsInstructions');
    },
    ownershipPercentage() {
      let section = `${this.isControlPerson ? 'controlPersonDetails' : 'beneficiaryDetails'}`;
      return this.$t(`businessOwnership.${section}.add.ownershipPercentage`);
    },
    maxDateOfBirth() {
      let date = new Date();
      date.setFullYear(date.getFullYear() - 18);
      return date;
    },
    ...mapGetters('stakeholdersDetails', [
      'getBeneficialOwnerType',
      'getBusinessName',
      'getAddressLine1',
      'getAddressLine2',
      'getAddressCity',
      'getAddressState',
      'getAddressZip',
      'getAddressCountry',
      'getFirstName',
      'getLastName',
      'getJobTitle',
      'getDateOfBirth',
      'getEmailAddress',
      'getPhoneNumber',
      'getOwnershipPercentage',
      'getTaxId',
      'shouldShowApiField',
      'getApi',
      'getErrors',
      'getErrorByField',
    ]),
    ...mapGetters('businessAddress', ['getCountry']),
  },
  mounted() {
    EventBus.$on(FormEvents.ENTER, this.addBeneficiary)
  },
  methods: {
    ...mapActions('stakeholdersDetails', [
      'setOwnerType',
      'setBusinessName',
      'setFirstName',
      'setLastName',
      'setJobTitle',
      'setDateOfBirth',
      'setEmailAddress',
      'setPhoneNumber',
      'setAddressLine1',
      'setAddressLine2',
      'setAddressCity',
      'setAddressState',
      'setAddressZip',
      'setAddressCountry',
      'setOwnershipPercentage',
      'setTaxId',
      'setRegistrationNumber',
      'setVatNumber',
      'validate',
      'clearErrors',
    ]),
    getMax: function (key) {
      return validations[this.getApi][key].length?.maximum
    },
    getMin: function (key) {
      return validations[this.getApi][key].length?.minimum
    },
    getMaxValue: function (key) {
      return validations[this.getApi][key].range?.maximum
    },
    getMinValue: function (key) {
      return validations[this.getApi][key].range?.minimum
    },
    addBeneficiary: function () {
      Promise.all([this.validate(this.i)])
          .then(values => {
            if (values.some(x => !x)) {
              this.$danger(this.$t('errors.validationErrorToastSave'))
            } else {
              this.close()
            }
          })
    },
    dismiss: function () {

      this.clearErrors()
      this.$emit('dismiss')
      this.close();
    },
    taxId(i) {
      if (this.isControlPerson) {
        if (this.getCountry !== Country.USA) {
          return this.$t('addBeneficiary.taxId.socialSecurityNumber')
        }
        return this.$t('addBeneficiary.taxId.label')
      }
      const label = this.getBeneficialOwnerType(i).value === 'ENTITY' ? "einAbbr" : "ssnAbbr"
      if (this.getCountry !== Country.USA) {
        return this.$t('addBeneficiary.taxId.socialSecurityNumber')
      }
      return this.$t(`addBeneficiary.taxId.${label}`);
    },
    taxIdInstruction(i) {
      if (this.isControlPerson) {
        return this.$t('addBeneficiary.taxId.instruction')
      }
      const label = this.getBeneficialOwnerType(i).value === 'ENTITY' ? "ein" : "ssn"
      return this.$t(`addBeneficiary.taxId.${label}`);
    },
    getMaxLengthForTaxId() {
      return 20;
    },
    getFormattedDate: (date) => {
      if (date === null) return null;
      return date.replace(/(\d{4})-(\d\d)-(\d\d)/, "$2/$3/$1");
    },
    getPhoneCountryCode(i) {
      if (this.getPhoneNumber(i).value && this.getPhoneNumber(i).value.includes(" ")) {
        return this.getPhoneNumber(i).value?.split(" ")[0];
      }
      return CountryCodeListSortedByName.find(x => x.value === this.getCountry)?.dialCode;
    },
  },
}
</script>

<style lang="scss">
.incremental-add-beneficiary-container {
  & .header {
    z-index: 99;
    .s-selectmenu-content {
      min-width: auto;
    }
  }
  
  & .phone-select {
    .s-selectmenu-content {
      min-width: 250px;
    }
  }
}


</style>
